import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Lock, Trash2 } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { GeldFeld, geldEingabe } from '@/components/geldfeld'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige, frage } from '@/lib/dialoge'
import { feldKlasse } from '@/lib/speichern'
import { leseZahl } from '@/lib/speichern/zahl'
import { addPayment, blockPayment, deletePayment, fetchPayments, releasePaymentBlock } from '../api/incoming-invoices.api'
import { tagText } from '../lib/texte'
import type { IncomingInvoiceDetail, IncomingPayment } from '../types/incoming-invoices.types'

const knopf = 'rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'
const WEG_TEXT: Record<string, string> = { MANUAL: 'von Hand', PAYMENT_RUN: 'Zahlungslauf', BANK_IMPORT: 'Kontoauszug' }
const STATUS: Record<string, string> = { NONE: 'noch nicht zahlbar', OPEN: 'offen', PARTIALLY_PAID: 'teilbezahlt', OVERDUE: 'überfällig', PAID: 'bezahlt' }

/**
 * AP-34 12.2, 12.5 — **Zahlung** an der freigegebenen Rechnung: was offen ist, die Zahlungen,
 * „Zahlung erfassen" von Hand (Datum, Betrag, Skonto) und die Zahlungssperre.
 *
 * Eine Zahlung ist ein Vorgang, der als Einheit erfasst wird — deshalb ein Knopf (`speichern.md`, Fall 3).
 */
export function Zahlung({ rechnung, onRechnung, onFehler }: { rechnung: IncomingInvoiceDetail; onRechnung: (r: IncomingInvoiceDetail) => void; onFehler: (t: string | null) => void }) {
  const { user } = useAuthStore()
  const darf = erfuelltRecht('incoming_invoices.payment.manage', user?.permissions)
  const darfIban = erfuelltRecht('incoming_invoices.payment.unblock', user?.permissions) && erfuelltRecht('crm.client.accounting.view', user?.permissions)
  const heute = new Date().toISOString().slice(0, 10)
  const skontoGilt = !!rechnung.discountDueDate && rechnung.discountDueDate >= heute && rechnung.paidAmount === 0 && rechnung.discountAmount > 0
  const [zahlungen, setZahlungen] = useState<IncomingPayment[]>([])
  const [datum, setDatum] = useState(heute)
  const [betrag, setBetrag] = useState('')
  const [skonto, setSkonto] = useState('')
  const [notiz, setNotiz] = useState('')
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    let aus = false
    fetchPayments(rechnung.id)
      .then((z) => !aus && setZahlungen(z))
      .catch(() => !aus && setZahlungen([]))
    return () => {
      aus = true
    }
  }, [rechnung.id, rechnung.paidAmount, rechnung.openAmount, rechnung.updatedAt])

  // Vorbelegung: das Offene — mit Skonto, solange die Frist läuft
  useEffect(() => {
    const s = skontoGilt ? rechnung.discountAmount : 0
    setBetrag(rechnung.openAmount > 0 ? geldEingabe(Math.round((rechnung.openAmount - s) * 100) / 100) : '')
    setSkonto(s ? geldEingabe(s) : '')
  }, [rechnung.openAmount, rechnung.discountAmount, skontoGilt])

  const tue = async (was: () => Promise<IncomingInvoiceDetail>, fehlertext: string) => {
    try {
      setLaeuft(true)
      onFehler(null)
      onRechnung(await was())
    } catch (e) {
      onFehler(apiErrorText(e, fehlertext))
    } finally {
      setLaeuft(false)
    }
  }

  const erfassen = async () => {
    const b = betrag.trim() ? leseZahl(betrag) : 0
    const s = skonto.trim() ? leseZahl(skonto) : 0
    if (b === null || s === null || Number.isNaN(b) || Number.isNaN(s) || (!b && !s)) return onFehler('Betrag und Skonto müssen Zahlen sein, und eines von beiden muss dastehen.')
    await tue(() => addPayment(rechnung.id, { paidOn: datum, ...(b && { amount: b }), ...(s && { discountAmount: s }), note: notiz.trim() || null }), 'Die Zahlung ließ sich nicht erfassen.')
    setNotiz('')
  }
  const loeschen = async (z: IncomingPayment) => {
    if (!(await bestaetige(`Zahlung über ${geldText(z.amount)} vom ${tagText(z.paidOn)} löschen?`, { ok: 'Löschen', gefaehrlich: true }))) return
    await tue(() => deletePayment(rechnung.id, z.id), 'Die Zahlung ließ sich nicht löschen.')
  }
  const sperren = async () => {
    const grund = await frage('Zahlung sperren — die Rechnung kommt in keinen Zahlungslauf, bis die Sperre gelöst ist. Grund:', '', { pflicht: true, ok: 'Sperren' })
    if (grund) await tue(() => blockPayment(rechnung.id, grund), 'Die Zahlung ließ sich nicht sperren.')
  }
  const nurDiese = async () => {
    const kommentar = await frage('Nur für diese Rechnung an die IBAN der Rechnung zahlen — der Lieferant behält seine bisherige. Wie hast du die IBAN geprüft?', '', { pflicht: true, ok: 'Sperre lösen' })
    if (kommentar) await tue(() => releasePaymentBlock(rechnung.id, { mode: 'THIS_ONLY', comment: kommentar }), 'Die Sperre ließ sich nicht lösen.')
  }
  const uebernehmen = async () => {
    if (!(await bestaetige(`IBAN ${rechnung.paymentIban ?? ''} am Lieferanten ${rechnung.supplierName ?? ''} übernehmen? Sie gilt dann für alle künftigen Zahlungen.`, { ok: 'IBAN übernehmen' }))) return
    await tue(() => releasePaymentBlock(rechnung.id, { mode: 'ADOPT' }), 'Die IBAN ließ sich nicht übernehmen.')
  }

  const iban = rechnung.paymentBlockedReason === 'IBAN_CHANGED'
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span>
          <span className="text-muted-foreground">offen </span>
          <span className="text-lg font-semibold tabular-nums">{geldText(rechnung.openAmount)}</span>
        </span>
        <span className="text-muted-foreground">
          bezahlt <span className="tabular-nums text-foreground">{geldText(rechnung.paidAmount)}</span> von <span className="tabular-nums">{geldText(rechnung.statedGross ?? rechnung.grossTotal)}</span>
        </span>
        <span className={rechnung.paymentStatus === 'OVERDUE' ? 'font-medium text-destructive' : 'text-muted-foreground'}>{STATUS[rechnung.paymentStatus] ?? rechnung.paymentStatus}</span>
        {rechnung.dueDate && <span className="text-muted-foreground">fällig {tagText(rechnung.dueDate)}</span>}
        {skontoGilt && (
          <span className="text-amber-700 dark:text-amber-300">
            Skonto {geldText(rechnung.discountAmount)} bis {tagText(rechnung.discountDueDate)}
          </span>
        )}
      </div>

      {rechnung.paymentBlocked && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
          <p className="flex items-center gap-2 font-medium text-destructive">
            <Lock size={14} /> Zahlung gesperrt{iban ? ': Die IBAN laut Rechnung weicht vom Lieferanten ab.' : rechnung.paymentBlockedReason ? `: ${rechnung.paymentBlockedReason}` : '.'}
          </p>
          {iban ? (
            <>
              <p className="mt-1 text-muted-foreground">
                IBAN laut Rechnung: <span className="font-mono text-foreground">{rechnung.paymentIban}</span>. Ruf den Lieferanten unter der <strong>bekannten</strong> Nummer zurück — nicht unter der auf der Rechnung.
              </p>
              {darfIban ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" className={knopf} disabled={laeuft} onClick={() => void uebernehmen()}>
                    IBAN am Lieferanten übernehmen
                  </button>
                  <button type="button" className={knopf} disabled={laeuft} onClick={() => void nurDiese()}>
                    Nur für diese Rechnung zahlen
                  </button>
                </div>
              ) : (
                <p className="mt-1 text-muted-foreground">Lösen kann die Sperre, wer Zahlungssperren aufheben und die Buchhaltungsfelder des Lieferanten sehen darf.</p>
              )}
            </>
          ) : (
            darf && (
              <button type="button" className={`${knopf} mt-2`} disabled={laeuft} onClick={() => void tue(() => releasePaymentBlock(rechnung.id, {}), 'Die Sperre ließ sich nicht lösen.')}>
                Sperre lösen
              </button>
            )
          )}
        </div>
      )}

      {zahlungen.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {zahlungen.map((z) => (
            <li key={z.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-1.5">
              <span className="tabular-nums">{tagText(z.paidOn)}</span>
              <span className="w-24 text-right font-medium tabular-nums">{geldText(z.amount)}</span>
              {z.isDiscount && <span className="rounded bg-amber-500/15 px-1.5 text-xs text-amber-700 dark:text-amber-300">Skonto</span>}
              <span className="text-xs text-muted-foreground">
                {WEG_TEXT[z.method] ?? z.method}
                {z.runId && (
                  <>
                    {' '}
                    <Link className="underline hover:text-foreground" to={`/incoming-invoices/payment-runs?run=${z.runId}`}>
                      {z.runNumber}
                    </Link>
                  </>
                )}
              </span>
              {(z.note || z.bankReference) && <span className="text-xs text-muted-foreground">{z.note ?? z.bankReference}</span>}
              {darf && z.deletable && (
                <button type="button" className="ml-auto rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" disabled={laeuft} aria-label={`Zahlung vom ${tagText(z.paidOn)} löschen`} title="Zahlung löschen" onClick={() => void loeschen(z)}>
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {darf && rechnung.paymentStatus !== 'PAID' && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-muted-foreground">
            gezahlt am
            <input type="date" aria-label="gezahlt am" className={`${feldKlasse} mt-0.5 w-40`} value={datum} onChange={(e) => setDatum(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">
            Betrag
            {/* Preisformat wie jeder Betrag (`geldbetraege.md`): ohne Fokus 1.234,50, beim Bearbeiten ohne Tausenderpunkt */}
            <GeldFeld aria-label="Betrag der Zahlung" className={`${feldKlasse} mt-0.5 w-32`} wert={betrag} onWert={setBetrag} />
          </label>
          <label className="text-xs text-muted-foreground">
            Skonto
            <GeldFeld aria-label="Skonto" className={`${feldKlasse} mt-0.5 w-28`} wert={skonto} onWert={setSkonto} />
          </label>
          <label className="min-w-[10rem] flex-1 text-xs text-muted-foreground">
            Notiz
            <input aria-label="Notiz zur Zahlung" className={`${feldKlasse} mt-0.5`} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
          </label>
          <button type="button" className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50" disabled={laeuft || !datum} onClick={() => void erfassen()}>
            Zahlung erfassen
          </button>
          {!rechnung.paymentBlocked && (
            <button type="button" className={knopf} disabled={laeuft} onClick={() => void sperren()}>
              Zahlung sperren
            </button>
          )}
        </div>
      )}
    </div>
  )
}
